import type { PublicCategoryStatus } from "@/v2-status-client/status/status.types"
import StatusCategoryServiceList from "@/v2-status-client/status/category/StatusCategoryServiceList"

export type StatusCategorySubGroupProps = {
  category: PublicCategoryStatus
}

// One sub-category grouping inside an expanded parent category
// (today: Map Layers' children — "Buildings", "Cadaster", …).
//
// Rendered as a flat labelled section, not a nested card. The
// outer accordion already provides the surrounding frame; adding
// another bordered box per sub-category produced visible
// "boxes-in-boxes" stacking that fought with the parent card and
// the per-row dividers.
//
// Visual separation between consecutive sub-groups comes from
// (a) the parent's flex gap, and (b) a thin top divider on each
// group except the first (`first:border-t-0`) so the very first
// group sits flush with the accordion content padding.
//
// The header is the `#category-{subId}` anchor target so a deep
// link from the sidebar lands directly on the sub-category. It's
// also `position: sticky` so it stays visible while the customer
// scrolls through that bucket; `top-32` sits just below the parent
// accordion heading (platform pill row + parent trigger height).
function StatusCategorySubGroup(props: Readonly<StatusCategorySubGroupProps>) {
  const { category } = props
  return (
    <section
      id={`category-${category.id}`}
      aria-label={category.name}
      className="border-t border-border/40 first:border-t-0 scroll-mt-32 pt-4 first:pt-0"
    >
      <header className="flex items-baseline gap-3 py-2 text-sm font-semibold">
        <span className="flex items-center gap-2">
          Category: <span>{category.name}</span>
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          {category.summary}
        </span>
      </header>
      <StatusCategoryServiceList services={category.services} />
    </section>
  )
}

export default StatusCategorySubGroup
